/**
 * Description: CPQ dashboard — system statistics, complexity charts, and classification tiles
 */

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Clock } from 'lucide-react'
import { api, System, ComplexityHistoryPoint } from '@services/api'
import { toast } from 'react-toastify'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  LineChart,
  Line,
  type BarShapeProps,
} from 'recharts'

const CPQDashboard: React.FC = () => {
  const navigate = useNavigate()
  const [systems, setSystems] = useState<System[]>([])
  const [complexityHistory, setComplexityHistory] = useState<ComplexityHistoryPoint[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadSystems()
    loadComplexityHistory()
  }, [])

  const loadSystems = async () => {
    try {
      setIsLoading(true)
      const data = await api.getAllSystems()
      setSystems(data)
    } catch (error) {
      toast.error('Failed to load systems')
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const loadComplexityHistory = async () => {
    try {
      const data = await api.getComplexityHistory()
      setComplexityHistory(data)
    } catch {
      // history chart is non-critical; fail silently
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const recentSystems = [...systems]
    .sort((a, b) => {
      const dateA = a.updated_at ? new Date(a.updated_at).getTime() : 0
      const dateB = b.updated_at ? new Date(b.updated_at).getTime() : 0
      return dateB - dateA
    })
    .slice(0, 3)

  const counts = {
    total: systems.length,
    supportDepth: {
      low: systems.filter((s) => (s.product_count || 0) < 5).length,
      medium: systems.filter((s) => (s.product_count || 0) >= 5 && (s.product_count || 0) < 20).length,
      high: systems.filter((s) => (s.product_count || 0) >= 20).length,
    },
    complexity: {
      low: systems.filter((s) => {
        const score = s.complexity?.complexity_score
        return score !== undefined && score !== null && Number(score) < 10
      }).length,
      medium: systems.filter((s) => {
        const score = s.complexity?.complexity_score
        return score !== undefined && score !== null && Number(score) >= 10 && Number(score) < 30
      }).length,
      high: systems.filter((s) => {
        const score = s.complexity?.complexity_score
        return score !== undefined && score !== null && Number(score) >= 30 && Number(score) < 60
      }).length,
      critical: systems.filter((s) => {
        const score = s.complexity?.complexity_score
        return score !== undefined && score !== null && Number(score) >= 60
      }).length,
    },
    acquisition: {
      acquisition: systems.filter((s) => s.acquisition_type === 'acquisition').length,
      homegrown: systems.filter((s) => s.acquisition_type === 'homegrown').length,
      other: systems.filter((s) => s.acquisition_type === 'other').length,
    },
    management: {
      bit: systems.filter((s) => s.management_type === 'BIT').length,
      cb: systems.filter((s) => s.management_type === 'CB').length,
      shared: systems.filter((s) => s.management_type === 'shared').length,
      other: systems.filter((s) => s.management_type === 'other').length,
    },
    geographic: {
      international: systems.filter((s) => s.geographic_scope === 'international').length,
      domestic: systems.filter((s) => s.geographic_scope === 'domestic').length,
      both: systems.filter((s) => s.geographic_scope === 'both').length,
      other: systems.filter((s) => s.geographic_scope === 'other').length,
    },
    security: {
      critical: systems.filter((s) => s.security_score === 'critical').length,
      high: systems.filter((s) => s.security_score === 'high').length,
      medium: systems.filter((s) => s.security_score === 'medium').length,
      low: systems.filter((s) => s.security_score === 'low').length,
    },
    compliance: {
      critical: systems.filter((s) => s.compliance_score === 'critical').length,
      high: systems.filter((s) => s.compliance_score === 'high').length,
      medium: systems.filter((s) => s.compliance_score === 'medium').length,
      low: systems.filter((s) => s.compliance_score === 'low').length,
    },
  }

  const supportDepthData = [
    { name: '0 to 5 Products', value: counts.supportDepth.low, color: '#dc2626' },
    { name: '5 to 20 Products', value: counts.supportDepth.medium, color: '#ca8a04' },
    { name: '20+ Products', value: counts.supportDepth.high, color: '#16a34a' },
  ].filter((item) => item.value > 0)

  const securityData = [
    { name: 'High', value: counts.security.high, color: '#ea580c' },
    { name: 'Medium', value: counts.security.medium, color: '#ca8a04' },
    { name: 'Low', value: counts.security.low, color: '#16a34a' },
  ].filter((item) => item.value > 0)

  const complianceData = [
    { name: 'High', value: counts.compliance.high, color: '#ea580c' },
    { name: 'Medium', value: counts.compliance.medium, color: '#ca8a04' },
    { name: 'Low', value: counts.compliance.low, color: '#16a34a' },
  ].filter((item) => item.value > 0)

  const systemsWithComplexity = systems.filter(
    (s) => s.complexity?.complexity_score !== undefined && s.complexity.complexity_score !== null,
  )
  const avgComplexityScore =
    systemsWithComplexity.length > 0
      ? (
          systemsWithComplexity.reduce((acc, s) => acc + Number(s.complexity?.complexity_score), 0) /
          systemsWithComplexity.length
        ).toFixed(1)
      : null

  const complexityData = [
    { name: 'Low (< 10)', value: counts.complexity.low, color: '#16a34a' },
    { name: 'Medium (10-30)', value: counts.complexity.medium, color: '#ca8a04' },
    { name: 'High (30-60)', value: counts.complexity.high, color: '#ea580c' },
    { name: 'Critical (≥ 60)', value: counts.complexity.critical, color: '#dc2626' },
  ].filter((item) => item.value > 0)

  if (isLoading) {
    return (
      <div className="card py-12 text-center">
        <div className="border-primary-600 inline-block h-12 w-12 animate-spin rounded-full border-b-2"></div>
        <p className="mt-4 text-gray-500 dark:text-gray-400">Loading dashboard...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-xl font-bold text-gray-900 sm:text-3xl dark:text-gray-100">System Inventory Dashboard</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Overview of all systems in the Digital Abstraction Layer
          </p>
        </div>
      </div>

      {/* Tracked Systems */}
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center space-x-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
            <Clock className="h-5 w-5" />
            <span>Tracked Systems ({counts.total})</span>
          </h3>
          <button
            onClick={() => navigate('/systems')}
            className="text-primary-600 hover:text-primary-800 cursor-pointer text-sm font-medium"
          >
            View All →
          </button>
        </div>

        {recentSystems.length === 0 ? (
          <div className="card py-8 text-center">
            <p className="text-gray-500 dark:text-gray-400">No systems yet. Create your first one!</p>
          </div>
        ) : (
          <div className="card overflow-hidden p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                      System Name
                    </th>
                    <th className="hidden px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase sm:table-cell dark:text-gray-400">
                      Business Owner
                    </th>
                    <th className="hidden px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase md:table-cell dark:text-gray-400">
                      Management
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                      Last Updated
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                  {recentSystems.map((system) => (
                    <tr
                      key={system.id}
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900"
                      onClick={() => navigate(`/systems/${system.id}`)}
                    >
                      <td className="max-w-[160px] px-4 py-4">
                        <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                          {system.system_name}
                        </div>
                      </td>
                      <td className="hidden px-4 py-4 text-sm text-gray-500 sm:table-cell dark:text-gray-400">
                        {system.business_owner || '-'}
                      </td>
                      <td className="hidden px-4 py-4 md:table-cell">
                        {system.management_type && (
                          <span className="inline-flex rounded-full bg-blue-100 px-2 py-1 text-xs leading-5 font-semibold text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                            {system.management_type}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
                        {formatDate(system.updated_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Complexity Score Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        {/* Complexity Score Chart */}
        <div className="lg:col-span-2">
          <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">Complexity score</h3>
          <div className="card">
            {complexityData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={complexityData} margin={{ top: 16, right: 8, left: -16, bottom: 60 }}>
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fontWeight: 600, fill: '#374151', dy: 20 }}
                    angle={-35}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6b7280' }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '6px', color: '#fff' }}
                    itemStyle={{ color: '#fff' }}
                    cursor={{ fill: 'rgba(0,0,0,0.05)' }}
                  />
                  <Bar
                    dataKey="value"
                    shape={(props: BarShapeProps) => (
                      <Rectangle {...props} fill={props.payload?.color} radius={[4, 4, 0, 0]} />
                    )}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="py-8 text-center text-gray-500 dark:text-gray-400">No data available</p>
            )}
          </div>
        </div>

        {/* Average Complexity Score */}
        <div>
          <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">Average complexity</h3>
          <div
            className={`card flex h-[calc(300px+2.75rem)] flex-col items-center justify-center border ${
              avgComplexityScore === null
                ? 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-white/5'
                : Number(avgComplexityScore) < 10
                  ? 'border-green-200 bg-green-50 dark:border-green-800/50 dark:bg-green-900/30'
                  : Number(avgComplexityScore) < 30
                    ? 'border-yellow-200 bg-yellow-50 dark:border-yellow-800/50 dark:bg-yellow-900/30'
                    : Number(avgComplexityScore) < 60
                      ? 'border-orange-200 bg-orange-50 dark:border-orange-800/50 dark:bg-orange-900/30'
                      : 'border-red-200 bg-red-50 dark:border-red-800/50 dark:bg-red-900/30'
            }`}
          >
            {avgComplexityScore !== null ? (
              <>
                <p
                  className={`text-6xl font-bold ${
                    Number(avgComplexityScore) < 10
                      ? 'text-green-700 dark:text-green-300'
                      : Number(avgComplexityScore) < 30
                        ? 'text-yellow-700 dark:text-yellow-300'
                        : Number(avgComplexityScore) < 60
                          ? 'text-orange-700 dark:text-orange-300'
                          : 'text-red-700 dark:text-red-300'
                  }`}
                >
                  {avgComplexityScore}
                </p>
                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                  avg. across {systemsWithComplexity.length} systems
                </p>
                <p
                  className={`mt-2 text-sm font-semibold ${
                    Number(avgComplexityScore) < 10
                      ? 'text-green-600'
                      : Number(avgComplexityScore) < 30
                        ? 'text-yellow-600'
                        : Number(avgComplexityScore) < 60
                          ? 'text-orange-600'
                          : 'text-red-600'
                  }`}
                >
                  {Number(avgComplexityScore) < 10
                    ? 'Low'
                    : Number(avgComplexityScore) < 30
                      ? 'Medium'
                      : Number(avgComplexityScore) < 60
                        ? 'High'
                        : 'Critical'}
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No complexity data</p>
            )}
          </div>
        </div>

        {/* Complexity Score History */}
        <div className="lg:col-span-2">
          <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">Average complexity over time</h3>
          <div className="card flex h-[calc(300px+2.75rem)] flex-col justify-center">
            {complexityHistory.length > 1 ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={complexityHistory} margin={{ top: 8, right: 16, left: -16, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    tickFormatter={(v: string) => {
                      const d = new Date(v + 'T00:00:00')
                      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    }}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6b7280' }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '6px', color: '#fff' }}
                    itemStyle={{ color: '#fff' }}
                    labelFormatter={(v) => {
                      const d = new Date(String(v) + 'T00:00:00')
                      return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                    }}
                    formatter={(value, _name, props) => [
                      `${value ?? '–'} (${(props.payload as ComplexityHistoryPoint)?.system_count ?? 0} system${(props.payload as ComplexityHistoryPoint)?.system_count !== 1 ? 's' : ''})`,
                      'Avg Complexity',
                    ]}
                  />
                  <Line
                    type="monotone"
                    dataKey="avg_score"
                    stroke="#6366f1"
                    strokeWidth={2}
                    dot={{ r: 3, fill: '#6366f1' }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : complexityHistory.length === 1 ? (
              <div className="flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                <p className="text-sm">Only one data point so far.</p>
                <p className="mt-1 text-xs">History will appear as more scores are recorded.</p>
              </div>
            ) : (
              <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                No history yet. Scores will be tracked as systems are updated.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Bar Charts Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div>
          <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">System Support Depth</h3>
          <div className="card">
            {supportDepthData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={supportDepthData} margin={{ top: 16, right: 8, left: -16, bottom: 60 }}>
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fontWeight: 600, fill: '#374151', dy: 20 }}
                    angle={-35}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6b7280' }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '6px', color: '#fff' }}
                    itemStyle={{ color: '#fff' }}
                    cursor={{ fill: 'rgba(0,0,0,0.05)' }}
                  />
                  <Bar
                    dataKey="value"
                    shape={(props: BarShapeProps) => (
                      <Rectangle {...props} fill={props.payload?.color} radius={[4, 4, 0, 0]} />
                    )}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="py-8 text-center text-gray-500 dark:text-gray-400">No data available</p>
            )}
          </div>
        </div>

        <div>
          <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">Security score</h3>
          <div className="card">
            {securityData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={securityData} margin={{ top: 16, right: 8, left: -16, bottom: 60 }}>
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fontWeight: 600, fill: '#374151', dy: 10 }}
                    angle={-35}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6b7280' }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '6px', color: '#fff' }}
                    itemStyle={{ color: '#fff' }}
                    cursor={{ fill: 'rgba(0,0,0,0.05)' }}
                  />
                  <Bar
                    dataKey="value"
                    shape={(props: BarShapeProps) => (
                      <Rectangle {...props} fill={props.payload?.color} radius={[4, 4, 0, 0]} />
                    )}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="py-8 text-center text-gray-500 dark:text-gray-400">No data available</p>
            )}
          </div>
        </div>

        <div>
          <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">Compliance score</h3>
          <div className="card">
            {complianceData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={complianceData} margin={{ top: 16, right: 8, left: -16, bottom: 60 }}>
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fontWeight: 600, fill: '#374151', dy: 10 }}
                    angle={-35}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6b7280' }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '6px', color: '#fff' }}
                    itemStyle={{ color: '#fff' }}
                    cursor={{ fill: 'rgba(0,0,0,0.05)' }}
                  />
                  <Bar
                    dataKey="value"
                    shape={(props: BarShapeProps) => (
                      <Rectangle {...props} fill={props.payload?.color} radius={[4, 4, 0, 0]} />
                    )}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="py-8 text-center text-gray-500 dark:text-gray-400">No data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Classification Tiles */}
      <div className="space-y-6">
        <div>
          <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">Systems Support Depth</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div
              className="card cursor-pointer border border-red-200 bg-red-50 transition-colors hover:bg-red-100 dark:border-red-800/50 dark:bg-red-900/30 dark:hover:bg-red-900/40"
              onClick={() => navigate('/systems?productCountMin=0&productCountMax=4')}
            >
              <p className="text-sm text-gray-600 dark:text-gray-400">0 to 5 Products</p>
              <p className="text-2xl font-bold text-red-700 hover:text-red-800 dark:text-red-300 dark:hover:text-red-300">
                {counts.supportDepth.low}
              </p>
            </div>
            <div
              className="card cursor-pointer border border-yellow-200 bg-yellow-50 transition-colors hover:bg-yellow-100 dark:border-yellow-800/50 dark:bg-yellow-900/30 dark:hover:bg-yellow-900/40"
              onClick={() => navigate('/systems?productCountMin=5&productCountMax=19')}
            >
              <p className="text-sm text-gray-600 dark:text-gray-400">5 to 20 Products</p>
              <p className="text-2xl font-bold text-yellow-700 hover:text-yellow-800 dark:text-yellow-300 dark:hover:text-yellow-300">
                {counts.supportDepth.medium}
              </p>
            </div>
            <div
              className="card cursor-pointer border border-green-200 bg-green-50 transition-colors hover:bg-green-100 dark:border-green-800/50 dark:bg-green-900/30 dark:hover:bg-green-900/40"
              onClick={() => navigate('/systems?productCountMin=20&productCountMax=999999')}
            >
              <p className="text-sm text-gray-600 dark:text-gray-400">20+ Products</p>
              <p className="text-2xl font-bold text-green-700 hover:text-green-800 dark:text-green-300 dark:hover:text-green-300">
                {counts.supportDepth.high}
              </p>
            </div>
          </div>
        </div>

        <div>
          <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">Development Origin</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="card border border-blue-200 bg-blue-50 dark:border-blue-800/50 dark:bg-blue-900/30">
              <p className="text-sm text-gray-600 dark:text-gray-400">Via Acquisition</p>
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{counts.acquisition.acquisition}</p>
            </div>
            <div className="card border border-green-200 bg-green-50 dark:border-green-800/50 dark:bg-green-900/30">
              <p className="text-sm text-gray-600 dark:text-gray-400">CB Homegrown</p>
              <p className="text-2xl font-bold text-green-700 dark:text-green-300">{counts.acquisition.homegrown}</p>
            </div>
            <div className="card border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-white/5">
              <p className="text-sm text-gray-600 dark:text-gray-400">Other</p>
              <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{counts.acquisition.other}</p>
            </div>
          </div>
        </div>

        <div>
          <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">BIT vs. CB Managed</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="card border border-blue-200 bg-blue-50 dark:border-blue-800/50 dark:bg-blue-900/30">
              <p className="text-sm text-gray-600 dark:text-gray-400">BIT/TPX Managed</p>
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{counts.management.bit}</p>
            </div>
            <div className="card border border-orange-200 bg-orange-50 dark:border-orange-800/50 dark:bg-orange-900/30">
              <p className="text-sm text-gray-600 dark:text-gray-400">CB Managed</p>
              <p className="text-2xl font-bold text-orange-700 dark:text-orange-300">{counts.management.cb}</p>
            </div>
            <div className="card border border-teal-200 bg-teal-50 dark:border-teal-800/50 dark:bg-teal-900/30">
              <p className="text-sm text-gray-600 dark:text-gray-400">Shared</p>
              <p className="text-2xl font-bold text-teal-700 dark:text-teal-300">{counts.management.shared}</p>
            </div>
            <div className="card border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-white/5">
              <p className="text-sm text-gray-600 dark:text-gray-400">Other</p>
              <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{counts.management.other}</p>
            </div>
          </div>
        </div>

        <div>
          <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">International vs. Domestic</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="card border border-blue-200 bg-blue-50 dark:border-blue-800/50 dark:bg-blue-900/30">
              <p className="text-sm text-gray-600 dark:text-gray-400">International</p>
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{counts.geographic.international}</p>
            </div>
            <div className="card border border-green-200 bg-green-50 dark:border-green-800/50 dark:bg-green-900/30">
              <p className="text-sm text-gray-600 dark:text-gray-400">Domestic</p>
              <p className="text-2xl font-bold text-green-700 dark:text-green-300">{counts.geographic.domestic}</p>
            </div>
            <div className="card border border-purple-200 bg-purple-50 dark:border-purple-800/50 dark:bg-purple-900/30">
              <p className="text-sm text-gray-600 dark:text-gray-400">Both</p>
              <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">{counts.geographic.both}</p>
            </div>
            <div className="card border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-white/5">
              <p className="text-sm text-gray-600 dark:text-gray-400">Other</p>
              <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{counts.geographic.other}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CPQDashboard
